import { PlusCircle } from "@phosphor-icons/react"
import { useCallback, useState } from "react"

import { Button, type ButtonProps } from "~/components/design-system/components"
import { DialogRoot, DialogTrigger } from "~/components/reusable/Dialog"
import { type ICollectionDetails, useAddVariantsToCollection } from "~/hooks/useCollections"
import { CollectionSource } from "~/models/collection"
import { api } from "~/utils/api"
import { errorMessageIncludes } from "~/utils/errorUtils"
import HandleAddVariantConflictContent from "./content/HandleAddVariantConflictContent"
import AddVariantsContent from "./content/AddVariantsContent"

export enum AddVariantStep {
  AddVariants = "addVariants",
  HandleConflict = "handleConflict",
}

type Props = {
  collection: ICollectionDetails
  initiallyOpen: boolean
  variantIdsInCollection: string[]
  disabled?: boolean
  /** Why the caller disabled it, shown on the trigger. Without one it disables silently. */
  disabledReason?: string
  size?: ButtonProps["size"]
  onOpenChange?: (open: boolean) => void
  /** Passed to the variant picker grid; see `AddVariantsContent`. */
  tableId?: string
}

export const AddVariantsToCollectionDialog = ({
  collection,
  initiallyOpen = false,
  variantIdsInCollection,
  disabled = false,
  disabledReason,
  size = "sm",
  onOpenChange,
  tableId,
}: Props) => {
  const fromErp = collection.source === CollectionSource.Erp
  const [open, setOpen] = useState(initiallyOpen && !fromErp)
  const { mutate: addVariants, isPending: isAddingVariants } = useAddVariantsToCollection()
  const [step, setStep] = useState<AddVariantStep>(AddVariantStep.AddVariants)
  const [selectedVariantIds, setSelectedVariantIds] = useState<string[]>([])
  const { data: suggestedVariantIds } = api.collection.getSuggestedVariants.useQuery(
    { collectionId: collection.id },
    { enabled: open && !fromErp && collection.externalId != null },
  )

  const handleOpenChange = useCallback(
    (open: boolean) => {
      setSelectedVariantIds([])
      setStep(AddVariantStep.AddVariants)
      setOpen(open)
      onOpenChange?.(open)
    },
    [onOpenChange],
  )

  const onSubmit = useCallback(
    (variantIds: string[]) => {
      setSelectedVariantIds(variantIds)
      addVariants(
        {
          collectionId: collection.id,
          variantIds,
        },
        {
          onSuccess: () => {
            setOpen(false)
            onOpenChange?.(false)
          },
          onError: (error) => {
            if (errorMessageIncludes(error, "do not match the specified collection type")) {
              setStep(AddVariantStep.HandleConflict)
            } else {
              setSelectedVariantIds([])
              setOpen(false)
              onOpenChange?.(false)
            }
          },
        },
      )
    },
    [addVariants, collection.id, onOpenChange],
  )

  const renderStepContent = () => {
    switch (step) {
      case AddVariantStep.AddVariants:
        return (
          <AddVariantsContent
            onAddVariants={onSubmit}
            variantIdsInUse={variantIdsInCollection}
            isLoading={isAddingVariants}
            isNoosCollection={collection.collectionType === "noos"}
            suggestedVariantIds={suggestedVariantIds}
            tableId={tableId}
          />
        )
      case AddVariantStep.HandleConflict:
        return (
          <HandleAddVariantConflictContent
            variantIds={selectedVariantIds}
            collectionId={collection.id}
            setOpen={handleOpenChange}
          />
        )
      default:
        return null
    }
  }

  return (
    <DialogRoot open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger
        requires="edit"
        tooltipSide="bottom"
        allowedTitle={fromErp ? "This collection's variants come from the ERP" : disabledReason}
        asChild
        disabled={disabled || fromErp}
      >
        <Button size={size}>
          <PlusCircle /> Add Variants
        </Button>
      </DialogTrigger>
      {open && renderStepContent()}
    </DialogRoot>
  )
}
